import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapIcon,
} from '@heroicons/react/24/outline';
import Meta from '../components/Meta';
import HomeFooter from '../components/HomeFooter';
import PageHeader from '../components/shell/PageHeader';
import CityFilter from '../components/CityFilter';
import ProfilePhoto from '../components/ProfilePhoto';
import { ShowCover } from '../components/ShowGrid';
import ShowsMapView from '../components/ShowsMapView';
import { Link } from '../util/router';
import { useCityStore } from '../stores';
import { cityLabel } from '../util/data-factory';
import { getFeaturedLists } from '../util/util';
import {
  getClosingSoonShows,
  getHighestRelevanceShows,
  getMostSeenShows,
  getOpeningSoonShows,
} from '../util/db';
import IMG_PLACEHOLDER from '../assets/images/placeholder1.png';

function Rail({ title, subtitle, action, children }) {
  const ref = useRef(null);
  const scroll = direction =>
    ref.current?.scrollBy({ left: direction * ref.current.clientWidth * 0.8, behavior: 'smooth' });

  return (
    <section className="mb-12">
      <div className="mb-4 flex items-end justify-between gap-4 px-4 md:px-8">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {action}
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scroll(-1)}
            className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-100 md:flex"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scroll(1)}
            className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-100 md:flex"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div
        ref={ref}
        className="no-scrollbar flex snap-x scroll-px-4 gap-4 overflow-x-auto scroll-smooth px-4 pb-2 md:scroll-px-8 md:gap-6 md:px-8"
      >
        {children}
      </div>
    </section>
  );
}

function FeaturedShowCard({ show }) {
  const venue = show.venues ?? {};
  return (
    <Link to={`/show/${show.show_id}`} className="group w-60 shrink-0 snap-start md:w-80">
      <div className="h-48 overflow-hidden rounded-lg bg-slate-100 md:h-60">
        <ShowCover show={show} className="transition-transform group-hover:scale-[1.03]" />
      </div>
      <p className="mt-2 line-clamp-2 text-sm font-bold text-slate-900 md:text-base">
        {show.display_title || show.show_title}
      </p>
      <p className="truncate text-xs text-slate-600 md:text-sm">
        {[venue.venue_name, venue.neighborhood].filter(Boolean).join(' | ')}
      </p>
    </Link>
  );
}

function FeaturedListCard({ list }) {
  return (
    <div className="w-40 shrink-0 snap-start md:w-56">
      <Link to={`/lists/${list.id}`} className="block">
        <div className="aspect-[6/7] overflow-hidden rounded-lg shadow-lg">
          <img
            src={list.cover_photo || IMG_PLACEHOLDER}
            alt=""
            className="h-full w-full object-cover transition-transform hover:scale-[1.03]"
          />
        </div>
        <p className="mt-2 truncate text-sm uppercase text-slate-900 md:text-base">
          {list.list_name}
        </p>
      </Link>
      {list.username && (
        <Link
          to={`/profile/${list.username}`}
          className="mt-1 flex items-center gap-2 text-sm text-slate-500 hover:opacity-80"
        >
          <ProfilePhoto userId={list.owner} isSettings={false} small />@{list.username}
        </Link>
      )}
    </div>
  );
}

function FeaturedPage() {
  const { selectedCity } = useCityStore();
  const cityName = cityLabel(selectedCity);

  const [lists, setLists] = useState(null);
  const [mostSeen, setMostSeen] = useState([]);
  const [cityShows, setCityShows] = useState(null);
  const [mapSection, setMapSection] = useState(null);

  useEffect(() => {
    getFeaturedLists()
      .then(data => setLists((data ?? []).filter(list => list.shows?.length)))
      .catch(err => {
        console.error(err);
        setLists([]);
      });
    getMostSeenShows().then(setMostSeen).catch(console.error);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCityShows(null);
    Promise.all([
      getHighestRelevanceShows(selectedCity),
      getOpeningSoonShows(selectedCity),
      getClosingSoonShows(selectedCity),
    ])
      .then(([top, opening, closing]) => !cancelled && setCityShows({ top, opening, closing }))
      .catch(err => {
        console.error(err);
        if (!cancelled) setCityShows({ top: [], opening: [], closing: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCity]);

  const showRail = (title, shows) =>
    shows?.length > 0 && (
      <Rail
        title={title}
        action={
          <button
            type="button"
            onClick={() => {
              setMapSection({ title, shows });
              window.scrollTo(0, 0);
            }}
            className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-100"
            aria-label={`See ${title} on a map`}
          >
            <MapIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Map</span>
          </button>
        }
      >
        {shows.map((show, index) => (
          <FeaturedShowCard key={`${show.show_id}-${index}`} show={show} />
        ))}
      </Rail>
    );

  return (
    <>
      <Meta title="Featured - Showrunner" />
      <PageHeader
        title="Featured"
        actions={
          <div className="hidden md:block">
            <CityFilter />
          </div>
        }
      >
        <div className="md:hidden">
          <CityFilter />
        </div>
      </PageHeader>

      {mapSection ? (
        <div className="mx-auto w-full max-w-[90rem] px-4 py-6 md:px-8 md:py-8">
          <div className="mb-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMapSection(null)}
              aria-label="Back to Featured"
              className="rounded-lg p-2 hover:bg-slate-100"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">{mapSection.title}</h2>
          </div>
          <ShowsMapView key={mapSection.title} shows={mapSection.shows} />
        </div>
      ) : (
        <div className="mx-auto w-full max-w-[90rem] py-8 md:py-10">
          <Rail
            title="Curated Lists"
            subtitle="Lists from Showrunner and the people who know the scene"
          >
            {lists === null ? (
              <span className="loading loading-spinner loading-md my-12 text-secondary-red" />
            ) : lists.length ? (
              lists.map(list => <FeaturedListCard key={list.id} list={list} />)
            ) : (
              <p className="py-8 text-slate-500">No featured lists yet.</p>
            )}
          </Rail>

          {showRail('Most Seen Shows', mostSeen)}

          {cityShows === null ? (
            <div className="flex justify-center py-12">
              <span className="loading loading-spinner loading-lg text-secondary-red" />
            </div>
          ) : (
            <>
              {showRail(`Top Shows in ${cityName}`, cityShows.top)}
              {showRail(`Opening Soon in ${cityName}`, cityShows.opening)}
              {showRail(`Closing Soon in ${cityName}`, cityShows.closing)}
            </>
          )}
        </div>
      )}
      <HomeFooter />
    </>
  );
}

export default FeaturedPage;
